/**
 * Generic hook for dashboard API queries with loading/error state management.
 * @module hooks
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useGeoMarkupStore } from '@/store';

interface UseDashboardQueryReturn<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Generic hook that fetches dashboard data with loading/error state.
 * Handles mount/unmount lifecycle to prevent state updates on unmounted components.
 */
export function useDashboardQuery<T>(
  fetcher: () => Promise<T>,
  errorMessage: string
): UseDashboardQueryReturn<T> {
  const currentWorkspaceId = useGeoMarkupStore(
    (state) => state.auth.currentWorkspaceId
  );
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetcher();
      if (isMountedRef.current) {
        setData(result);
      }
    } catch (fetchError) {
      console.error('[Dashboard]', errorMessage, fetchError);
      if (isMountedRef.current) {
        setError(
          fetchError instanceof Error ? fetchError.message : errorMessage
        );
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [fetcher, errorMessage]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchData();
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData, currentWorkspaceId]);

  return { data, isLoading, error, refresh: fetchData };
}
